import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import authApi from '../../api/auth';
import adminApi from '../../api/admin';
import { useAuth } from '../../context/AuthContext';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    userCount: 0,
    roleCount: 0,
    queryCount: 0,
    queryErrorRate: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentQueries, setRecentQueries] = useState([]);
  
  const { user } = useAuth();
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Initialize default values
        let totalUserCount = 0;
        let users = [];
        let roles = [];
        let queryStats = { total_queries: 0, error_rate: 0 };
        let queries = [];
        
        // Fetch data with individual error handling
        const results = await Promise.allSettled([
          authApi.getUsers(0, 100),
          adminApi.getRoles(),
          adminApi.getQueryStats(),
          adminApi.getAllQueries(10)
        ]);
        
        // Process results with fallbacks
        if (results[0].status === 'fulfilled') {
          const usersResponse = results[0].value;
          users = usersResponse.users || [];
          totalUserCount = usersResponse.total || 0;
        } else {
          console.warn('Failed to fetch users:', results[0].reason);
        }
        
        if (results[1].status === 'fulfilled') {
          roles = results[1].value || [];
        } else {
          console.warn('Failed to fetch roles:', results[1].reason);
        }
        
        if (results[2].status === 'fulfilled') {
          queryStats = results[2].value;
        } else {
          console.warn('Failed to fetch query stats:', results[2].reason);
        }
        
        if (results[3].status === 'fulfilled') {
          queries = results[3].value || [];
        } else {
          console.warn('Failed to fetch recent queries:', results[3].reason);
        }
        
        // Sort users by created_at (most recent first) and take top 5
        const sortedUsers = [...users].sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        ).slice(0, 5);
        
        // Queries are already sorted by the backend, just take first 5
        const sortedQueries = queries.slice(0, 5);
        
        setStats({
          userCount: totalUserCount,
          roleCount: roles.length,
          queryCount: queryStats.total_queries || 0,
          queryErrorRate: queryStats.error_rate || 0
        });
        
        setRecentUsers(sortedUsers);
        setRecentQueries(sortedQueries);
        
      } catch (err) {
        console.error('Unexpected error in dashboard:', err);
        // Only set error if all critical operations failed
        setError('Some dashboard data could not be loaded.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  if (isLoading) {
    return (
      <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
        <div className="flex justify-center">
          <svg className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="mt-2 text-center text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Loading dashboard data...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
        <div className="p-4 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md border border-status-error-light-text/20 dark:border-status-error-dark-text/20">
          <p className="font-medium">Error:</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
      <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">Admin Dashboard</h1>
      <p className="mt-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
        Welcome back, {user.full_name || user.username}! Here's what's happening in your data portal.
      </p>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 mt-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="p-5 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow border border-ui-light-border dark:border-ui-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">{stats.userCount}</div>
              <div className="text-sm font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Total Users</div>
            </div>
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-full">
              <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-2">
            <Link to="/admin/users" className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
              Manage Users
            </Link>
          </div>
        </div>
        
        <div className="p-5 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow border border-ui-light-border dark:border-ui-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">{stats.roleCount}</div>
              <div className="text-sm font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Roles</div>
            </div>
            <div className="p-3 bg-status-success-light-bg dark:bg-status-success-dark-bg rounded-full">
              <svg className="w-6 h-6 text-status-success-light-text dark:text-status-success-dark-text" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
          </div>
          <div className="mt-2">
            <Link to="/admin/roles" className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
              Manage Roles
            </Link>
          </div>
        </div>
        
        <div className="p-5 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow border border-ui-light-border dark:border-ui-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">{stats.queryCount}</div>
              <div className="text-sm font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Total Queries</div>
            </div>
            <div className="p-3 bg-status-warning-light-bg dark:bg-status-warning-dark-bg rounded-full">
              <svg className="w-6 h-6 text-status-warning-light-text dark:text-status-warning-dark-text" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
          <div className="mt-2">
            <Link to="/queries/history" className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
              View Query History
            </Link>
          </div>
        </div>
        
        <div className="p-5 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow border border-ui-light-border dark:border-ui-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">{stats.queryErrorRate}%</div>
              <div className="text-sm font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Query Error Rate</div>
            </div>
            <div className="p-3 bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-full">
              <svg className="w-6 h-6 text-status-error-light-text dark:text-status-error-dark-text" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-2">
            <Link to="/queries/history?status=failed" className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
              View Failed Queries
            </Link>
          </div>
        </div>
      </div>
      
      {/* Recent Users */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Recent Users</h2>
          <Link to="/admin/users" className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
            View All
          </Link>
        </div>
        
        <div className="overflow-x-auto border border-ui-light-border dark:border-ui-dark-border rounded-md">
          <table className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
            <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
              <tr>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                  Created
                </th>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
              {recentUsers.map((user) => (
                <tr key={user.id} className="hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-8 h-8">
                        <div className="flex items-center justify-center w-8 h-8 text-white bg-primary-600 dark:bg-primary-700 rounded-full">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                          {user.username}
                        </div>
                        <div className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                          {user.full_name || '-'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.is_active ? (
                      <span className="inline-flex px-2 text-xs font-semibold leading-5 text-status-success-light-text dark:text-status-success-dark-text bg-status-success-light-bg dark:bg-status-success-dark-bg rounded-full">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex px-2 text-xs font-semibold leading-5 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-full">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <Link to={`/admin/users/${user.id}`} className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Recent Queries */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Recent Queries</h2>
          <Link to="/queries/history" className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
            View All
          </Link>
        </div>
        
        <div className="overflow-x-auto border border-ui-light-border dark:border-ui-dark-border rounded-md">
          <table className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
            <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
              <tr>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                  Query
                </th>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
              {recentQueries.map((query) => (
                <tr key={query.id} className="hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      {query.user ? query.user.username : 'Anonymous'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary max-w-xs truncate">
                      {query.query_text}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full 
                      ${query.status === 'completed' ? 'bg-status-success-light-bg dark:bg-status-success-dark-bg text-status-success-light-text dark:text-status-success-dark-text' : 
                      query.status === 'failed' ? 'bg-status-error-light-bg dark:bg-status-error-dark-bg text-status-error-light-text dark:text-status-error-dark-text' : 
                      'bg-status-warning-light-bg dark:bg-status-warning-dark-bg text-status-warning-light-text dark:text-status-warning-dark-text'}`}>
                      {query.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                    {new Date(query.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <Link to={`/queries/${query.id}`} className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Quick Actions</h2>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Link
            to="/admin/users"
            className="flex items-center p-4 transition-all duration-150 ease-in-out bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-lg shadow-sm hover:shadow hover:border-primary-300 dark:hover:border-primary-600"
          >
            <div className="p-3 mr-4 bg-primary-100 dark:bg-primary-900/30 rounded-full">
              <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Add New User</h3>
              <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Create a new user account</p>
            </div>
          </Link>
          
          <Link
            to="/admin/roles"
            className="flex items-center p-4 transition-all duration-150 ease-in-out bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-lg shadow-sm hover:shadow hover:border-primary-300 dark:hover:border-primary-600"
          >
            <div className="p-3 mr-4 bg-status-success-light-bg dark:bg-status-success-dark-bg rounded-full">
              <svg className="w-6 h-6 text-status-success-light-text dark:text-status-success-dark-text" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Manage Roles</h3>
              <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Create or modify user roles</p>
            </div>
          </Link>
          
          <Link
            to="/database"
            className="flex items-center p-4 transition-all duration-150 ease-in-out bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-lg shadow-sm hover:shadow hover:border-primary-300 dark:hover:border-primary-600"
          >
            <div className="p-3 mr-4 bg-status-warning-light-bg dark:bg-status-warning-dark-bg rounded-full">
              <svg className="w-6 h-6 text-status-warning-light-text dark:text-status-warning-dark-text" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Explore Database</h3>
              <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Browse available schemas and tables</p>
            </div>
          </Link>
          
          <Link
            to="/admin/astro-tables"
            className="flex items-center p-4 transition-all duration-150 ease-in-out bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-lg shadow-sm hover:shadow hover:border-primary-300 dark:hover:border-primary-600"
          >
            <div className="p-3 mr-4 bg-primary-100 dark:bg-primary-900/30 rounded-full">
              <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Astro Tables</h3>
              <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Manage astronomical tables for cone search</p>
            </div>
          </Link>
          
          <Link
            to="/admin/images"
            className="flex items-center p-4 transition-all duration-150 ease-in-out bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-lg shadow-sm hover:shadow hover:border-primary-300 dark:hover:border-primary-600"
          >
            <div className="p-3 mr-4 bg-status-info-light-bg dark:bg-status-info-dark-bg rounded-full">
              <svg className="w-6 h-6 text-status-info-light-text dark:text-status-info-dark-text" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Image Collections</h3>
              <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Manage astronomical image collections</p>
            </div>
          </Link>
          
          <Link
            to="/admin/news"
            className="flex items-center p-4 transition-all duration-150 ease-in-out bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-lg shadow-sm hover:shadow hover:border-primary-300 dark:hover:border-primary-600"
          >
            <div className="p-3 mr-4 bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-full">
              <svg className="w-6 h-6 text-status-error-light-text dark:text-status-error-dark-text" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">News Management</h3>
              <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Create and manage role-specific news posts</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;